import { z } from 'zod'
import { MAX_UPLOAD_COUNT ,MIN_UPLOAD_COUNT} from './constants'

const PHOTO_MAX = MAX_UPLOAD_COUNT
export const licensePlateRegex =
  /(\w{2}-\d{2}-\d{2})|(\d{2}-\d{2}-\w{2})|(\d{2}-\w{2}-\d{2})|(\w{2}-\d{2}-\w{2})|(\w{2}-\w{2}-\d{2})|(\d{2}-\w{2}-\w{2})|(\d{2}-\w{3}-\d{1})|(\d{1}-\w{3}-\d{2})|(\w{2}-\d{3}-\w{1})|(\w{1}-\d{3}-\w{2})|(\w{3}-\d{2}-\w{1})|(\w{1}-\d{2}-\w{3})|(\d{1}-\w{2}-\d{3})|(\d{3}-\w{2}-\d{1})/i

const DamageLocationSchema = z
  .object({
    own: z.boolean(),
    other: z.boolean(),
    object: z.boolean(),
  })
  .refine((data) => data.own || data.other || data.object, {
    message: 'validation.required_damage_location',
    path: ['damageLocation'],
  })

export const stepZeroSchema = z.object({
  injured: z
    .boolean()
    .nullable()
    .refine((val) => val !== null, {
      message: 'validation.required_injured',
    }),
})

export const getStepOneSchema = () =>
  z
    .object({
      damageLocation: DamageLocationSchema,
      involved: z.boolean().nullable().optional(),
      counterpartyContact: z.string().optional(),
      whatHappened: z.string().optional(),
      licensePlate: z.string().optional(),
      vehicleOperational: z.boolean().optional(),
      damageClassification: z.string().optional(),
      damageSide: z.string().optional(),
      vehicleId: z.string().optional(),
      vehicleCoordinates: z
        .tuple([z.number(), z.number()])
        .optional()
        .nullable(),
      photosOther: z.any().optional(),
      photosOwn: z.any().optional(),
      photosObject: z.any().optional(),
      photosEnvironment: z.any().optional(),
      formPhoto: z.any().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.damageSide) {
        if (data.damageLocation?.own) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'validation.required_damage_side',
            path: ['damageSide'],
          })
        }
      }
      if (!data.damageClassification && data.damageLocation?.own) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.required_damage_classification',
          path: ['damageClassification'],
        })
      }
      if (!data.vehicleCoordinates && data.damageLocation?.own) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.required_vehicle_coordinates',
          path: ['vehicleCoordinates'],
        })
      }

      if (data.damageLocation.other && !data.licensePlate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.required_license_plate',
          path: ['licensePlate'],
        })
      }

      if (data.damageLocation.other && data.licensePlate) {
        const isValid = licensePlateRegex.test(data.licensePlate)
        if (!isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'validation.invalid_pnumber',
            path: ['licensePlate'],
          })
        }
      }

      const getLength = (value: unknown): number => {
        if (!value) return 0
        const maybe = value as { length?: number }
        return typeof maybe.length === 'number' ? maybe.length : 0
      }

      const ownCount = getLength(data.photosOwn)
      const otherCount = getLength(data.photosOther)
      const objectCount = getLength(data.photosObject)
      const envCount = getLength(data.photosEnvironment)
      const formPhotoCount = getLength(data.formPhoto)

      if (data.involved === true && formPhotoCount < MIN_UPLOAD_COUNT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.required_form_photo',
          path: ['formPhoto'],
        })
      }

      if (ownCount > PHOTO_MAX) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.photos_max',
          path: ['photosOwn'],
        })
      }
      if (otherCount > PHOTO_MAX) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.photos_max',
          path: ['photosOther'],
        })
      }
      if (objectCount > PHOTO_MAX) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.photos_max',
          path: ['photosObject'],
        })
      }
      if (envCount > PHOTO_MAX) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.photos_max',
          path: ['photosEnvironment'],
        })
      }
    })
